/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import {
    INssProductFeatureGridButtonProps,
    INssProductFeatureGridContentLayoutProps,
    INssProductFeatureGridDescriptionProps,
    INssProductFeatureGridHeadingProps,
    INssProductFeatureGridLayoutProps,
    INssProductFeatureGridSubHeadingProps,
    INssProductFeatureGridViewProps
} from './nss-product-feature-grid';
import { LayoutFlexDirection, LayoutJustifyContent } from './nss-product-feature-grid.props.autogenerated';

export default (props: INssProductFeatureGridViewProps) => {
    const itemWidth: number = 100.0 / props.layout.columnCount;
    const rowCount: number = Math.ceil(props.content.length / props.layout.columnCount);
    const itemHeight: number = 100.0 / rowCount;

    return (
        <a className={`nss-product-feature__grid ${getCss(props.layout)}`} style={getGlobalStyle(props)} href={props.global.link}>
            {props.global.backgroundImage && (
                <div className='nss-product-feature__grid-background-image-wrap'>
                    <img src={props.global.backgroundImage.src} />
                </div>
            )}
            {props.content.map((gridItem, index) => (
                <div
                    className={`nss-product-feature__grid-item-wrap nss-product-feature__grid-align-${gridItem.layout.anchor.toString()}`}
                    key={index}
                    style={{ backgroundColor: gridItem.backgroundColor, width: `${itemWidth}%`, height: `${itemHeight}%` }}
                >
                    <div className='nss-product-feature__grid-item' style={getGridItemStyle(gridItem.layout)}>
                        {gridItem.heading && (
                            <div
                                className={`nss-product-feature__grid-item-heading nss-product-feature__grid-font-size-${gridItem.heading.size.toString()}`}
                                style={getHeadingStyle(gridItem.heading)}
                            >
                                {gridItem.heading.text}
                            </div>
                        )}
                        {gridItem.subHeading && (
                            <div
                                className={`nss-product-feature__grid-item-sub-heading nss-product-feature__grid-font-size-${gridItem.subHeading.size.toString()}`}
                                style={getSubHeadingStyle(gridItem.subHeading)}
                            >
                                {gridItem.subHeading.text}
                            </div>
                        )}
                        {gridItem.description && (
                            <div
                                className={`nss-product-feature__grid-item-description nss-product-feature__grid-font-size-${gridItem.description.size.toString()}`}
                                style={getDescriptionStyle(gridItem.description)}
                                dangerouslySetInnerHTML={{ __html: gridItem.description.text }}
                            ></div>
                        )}
                        {gridItem.button && (
                            <a
                                className={`nss-product-feature__grid-item-button nss-product-feature__grid-font-size-${gridItem.button.size.toString()}`}
                                style={{ alignSelf: gridItem.button.alignSelf.toString() }}
                                href={gridItem.button.link}
                            >
                                <button className='nss-product-feature__grid-item-button' style={getButtonStyle(gridItem.button)}>
                                    {gridItem.button.text}
                                </button>
                            </a>
                        )}
                        {gridItem.foregroundImageUrl && (
                            <div className='nss-product-feature__grid-item-image'>
                                <img src={gridItem.foregroundImageUrl} />
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </a>
    );
};

interface IAlignable {
    textAlign: {
        toString: () => string;
    };
}

const getTextAlign = (prop: IAlignable): 'left' | 'center' | 'right' | undefined => {
    const align: string = prop.textAlign.toString();
    switch (align) {
        case 'left':
        case 'center':
        case 'right':
            return align;
        default:
            return undefined;
    }
};

const getCss = (props: INssProductFeatureGridLayoutProps): string => {
    return props.cssClassName;
};

const getGlobalStyle = (props: INssProductFeatureGridViewProps): React.CSSProperties => {
    return {
        height: `max(${props.layout.height}vh, ${props.layout.height * 0.6}vw)`,
        backgroundColor: props.global.backgroundColor,
        color: props.global.fontColor,
        fontFamily: props.global.fontFamily
    };
};

const getHeadingStyle = (props: INssProductFeatureGridHeadingProps): React.CSSProperties => {
    return {
        fontWeight: props.bold ? 'bold' : undefined,
        fontStyle: props.italics ? 'italic' : undefined,
        textDecoration: props.underline ? 'underline' : undefined,
        fontFamily: props.fontFamily,
        textAlign: getTextAlign(props),
        color: props.fontColor,
        lineHeight: props.lineHeight ? `${props.lineHeight}em` : undefined,
        letterSpacing: props.letterSpacing ? `${props.letterSpacing}em` : undefined
    };
};

const getSubHeadingStyle = (props: INssProductFeatureGridSubHeadingProps): React.CSSProperties => {
    return {
        fontWeight: props.bold ? 'bold' : undefined,
        fontStyle: props.italics ? 'italic' : undefined,
        textDecoration: props.underline ? 'underline' : undefined,
        fontFamily: props.fontFamily,
        textAlign: getTextAlign(props),
        color: props.fontColor,
        lineHeight: props.lineHeight ? `${props.lineHeight}em` : undefined,
        letterSpacing: props.letterSpacing ? `${props.letterSpacing}em` : undefined
    };
};

const getDescriptionStyle = (props: INssProductFeatureGridDescriptionProps): React.CSSProperties => {
    return {
        fontWeight: props.bold ? 'bold' : undefined,
        fontStyle: props.italics ? 'italic' : undefined,
        textDecoration: props.underline ? 'underline' : undefined,
        fontFamily: props.fontFamily,
        textAlign: getTextAlign(props),
        color: props.fontColor,
        lineHeight: props.lineHeight ? `${props.lineHeight}em` : undefined,
        letterSpacing: props.letterSpacing ? `${props.letterSpacing}em` : undefined
    };
};

const getButtonStyle = (props: INssProductFeatureGridButtonProps): React.CSSProperties => {
    return {
        fontWeight: props.bold ? 'bold' : undefined,
        fontStyle: props.italics ? 'italic' : undefined,
        textDecoration: props.underline ? 'underline' : undefined,
        fontFamily: props.fontFamily,
        color: props.fontColor,
        lineHeight: props.lineHeight ? `${props.lineHeight}em` : undefined,
        letterSpacing: props.letterSpacing ? `${props.letterSpacing}em` : undefined,
        backgroundColor: props.backgroundColor
    };
};

const getGridItemStyle = (props: INssProductFeatureGridContentLayoutProps): React.CSSProperties => {
    const style: any = {
        width: `${props.width}%`,
        height: `${props.height}%`,
        zIndex: props.zIndex,
        padding: props.padding,
        alignItems: props.alignItems.toString()
    };

    switch (props.flexDirection) {
        case LayoutFlexDirection.row:
            style.flexDirection = 'row';
            break;
        case LayoutFlexDirection.rowReverse:
            style.flexDirection = 'row-reverse';
            break;
        case LayoutFlexDirection.column:
            style.flexDirection = 'column';
            break;
        case LayoutFlexDirection.columnReverse:
            style.flexDirection = 'column-reverse';
    }

    switch (props.justifyContent) {
        case LayoutJustifyContent.start:
            style.justifyContent = 'start';
            break;
        case LayoutJustifyContent.spaceBetween:
            style.justifyContent = 'space-between';
            break;
        case LayoutJustifyContent.spaceAround:
            style.justifyContent = 'space-around';
            break;
        case LayoutJustifyContent.center:
            style.justifyContent = 'center';
            break;
        case LayoutJustifyContent.end:
            style.justifyContent = 'end';
            break;
    }

    return style;
};
